'use client';

import React, { useState } from 'react';
import FacebookIcon from '../Icons/FacebookIcon';
import MailIcon from '../Icons/MailIcon';
import LinkIcon from '../Icons/LinkIcon';

const shareButtons = [
  {
    label: 'Share on Facebook',
    type: 'facebook',
    Icon: FacebookIcon,
  },
  {
    label: 'Share via Email',
    type: 'email',
    Icon: MailIcon,
  },
  {
    label: 'Copy Link',
    type: 'copy',
    Icon: LinkIcon,
  },
] as const;

type ShareButtonsProps = {
  layout?: 'mobile' | 'desktop';
  title: string;
};

const ShareButtons = ({ layout = 'mobile', title }: ShareButtonsProps) => {
  const [copied, setCopied] = useState(false);

  const handleShare = async (type: 'facebook' | 'email' | 'copy') => {
    const url = window.location.href;
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);

    if (type === 'facebook') {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        '_blank',
        'noopener,noreferrer',
      );
    }

    if (type === 'email') {
      window.location.href = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
    }

    if (type === 'copy') {
      await navigator.clipboard.writeText(url);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  return (
    <section
      aria-label='Share this article'
      className={
        layout === 'mobile'
          ? 'md:hidden flex w-full justify-center mb-6'
          : 'hidden md:flex relative flex-col items-center w-24'
      }
    >
      <div className={layout === 'mobile' ? 'flex items-center max-w-176.5 w-full' : ''}>
        <span
          className={
            layout === 'mobile'
              ? 'text-sm font-semibold mr-4 text-primary-500'
              : 'text-sm font-semibold mb-6 text-primary-500 block'
          }
        >
          Share
        </span>

        <div className={layout === 'mobile' ? 'flex gap-4' : 'flex flex-col gap-4'}>
          {shareButtons.map(({ label, type, Icon }) => (
            <div
              key={label}
              className='relative'
            >
              <button
                type='button'
                onClick={() => handleShare(type)}
                className='w-8 h-8 rounded-full border-[1.5px] border-secondary-500 flex items-center justify-center transition-colors hover:bg-secondary-500 hover:text-white group cursor-pointer'
                aria-label={label}
              >
                <Icon className='w-4.5 h-4.5 text-brand-icon group-hover:text-white transition-colors' />
              </button>

              {type === 'copy' && copied && (
                <span
                  role='status'
                  className='absolute whitespace-nowrap bg-primary-500 text-white text-xs px-2 py-1 rounded'
                >
                  Link copied
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ShareButtons;
